// src/components/PublicNavbar.tsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { GlobalStyles } from "../styles/globalStyles";
import { Menu, X, Moon, Sun, Sparkles, ChevronRight } from "lucide-react";
import logo from "../assets/images/logo.png";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { toggleTheme, setTheme } from "../redux/slices/themeSlice";

interface PublicNavbarProps {
  currentPage?: string;
}

const PublicNavbar: React.FC<PublicNavbarProps> = ({ currentPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dispatch = useDispatch();
  const { mode } = useSelector((state: RootState) => state.theme);

  useEffect(() => {
    const storedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;

    if (storedTheme === "dark" || (!storedTheme && prefersDark)) {
      dispatch(setTheme("dark"));
    } else {
      dispatch(setTheme("light"));
    }
  }, [dispatch]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleDarkMode = () => {
    dispatch(toggleTheme());
  };

  const navigationItems = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Privacy", path: "/privacy" },
    { name: "Terms", path: "/terms" },
  ];

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-b border-gray-200/30 dark:border-gray-800/30 shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group/logo transition-transform hover:scale-[1.02]"
          >
            <div className="relative">
              <img
                src={logo}
                alt="Pelivor Recovery"
                className="h-14 w-auto transition-all duration-500 group-hover/logo:rotate-3"
              />
              <div className="absolute -top-1 -right-1 h-4 w-4 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full animate-pulse border-2 border-white dark:border-gray-900"></div>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-bold bg-gradient-to-r from-primary to-primary-600 bg-clip-text text-transparent">
                Pelivor
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 tracking-wider">
                RECOVERY SERVICES
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 group/nav ${
                  currentPage === item.path
                    ? "text-primary"
                    : "text-gray-700 dark:text-gray-300 hover:text-primary"
                }`}
              >
                <span className="relative z-10">{item.name}</span>
                {currentPage === item.path && (
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl border border-primary/20"></div>
                )}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-0 bg-gradient-to-r from-primary to-primary-400 group-hover/nav:w-full transition-all duration-300"></div>
              </Link>
            ))}

            <div className="flex items-center space-x-3 ml-6">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className={`${GlobalStyles.iconButton} hover:bg-primary/10`}
              >
                {mode === "dark" ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </button>

              <div className="h-6 w-px bg-gray-300 dark:bg-gray-700"></div>

              {/* Auth Buttons */}
              <Link
                to="/login"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} group/login`}
              >
                <span>Sign In</span>
                <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover/login:translate-x-1" />
              </Link>

              <Link
                to="/register"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} group/register`}
              >
                <span>Get Started</span>
                <Sparkles className="w-4 h-4 ml-2 opacity-0 group-hover/register:opacity-100 transition-all duration-300 group-hover/register:translate-x-1" />
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-3">
            <button
              onClick={toggleDarkMode}
              className={`${GlobalStyles.iconButton} hover:bg-primary/10`}
            >
              {mode === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`${GlobalStyles.iconButton} hover:bg-primary/10`}
            >
              {isMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 pt-4 border-t border-gray-200/30 dark:border-gray-800/30 backdrop-blur-xl">
            <div className="space-y-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-300 group/mobile ${
                    currentPage === item.path
                      ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
                  }`}
                >
                  <span className="font-medium">{item.name}</span>
                  <ChevronRight className="h-4 w-4 opacity-0 group-hover/mobile:opacity-100 transition-opacity" />
                </Link>
              ))}

              <div className="pt-4 space-y-3">
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonMedium} ${GlobalStyles.buttonOutline} w-full justify-center group/login-mobile`}
                >
                  <span>Sign In</span>
                  <ChevronRight className="w-4 h-4 ml-2 opacity-0 group-hover/login-mobile:opacity-100 transition-opacity" />
                </Link>

                <Link
                  to="/register"
                  onClick={() => setIsMenuOpen(false)}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonMedium} ${GlobalStyles.buttonPrimary} w-full justify-center group/register-mobile`}
                >
                  <span>Get Started Free</span>
                  <Sparkles className="w-4 h-4 ml-2 opacity-0 group-hover/register-mobile:opacity-100 transition-opacity" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default PublicNavbar;
